/**
 * @file `<Aside/>` — canonical admonition block for chapter prose.
 * @module @labcraft/course-kit/components/aside
 *
 * The interactive-book counterpart of GitHub/DocBook admonitions:
 * `note` / `tip` / `important` / `warning` / `caution`, plus a
 * course-kit-specific `classic` variant for the recurring
 * modern↔classic-CLI footnote — the lightweight instance of the
 * history-of-evolution tenet (`CONTENT_REBUILD_BRIEF.md` § "Teach The
 * Evolution"; the full timeline surface is `<Evolution/>`).
 *
 * Pure render — no extractor, scanner, or lab-state effect. Injected
 * via `<MdxProvider/>`'s component map, so chapters write:
 *
 *   <Aside variant="tip">`nix flake show` prints the outputs.</Aside>
 */

import type { JSX, ReactNode } from 'react'
import {
  AlertTriangleIcon,
  HistoryIcon,
  InfoCircleIcon,
  LightbulbIcon,
  OctagonAlertIcon,
  ReportIcon,
} from './icons.tsx'
import styles from './aside.module.css'

/** The canonical admonition set + course-kit's `classic` footnote. */
export type AsideVariant =
  | 'note'
  | 'tip'
  | 'important'
  | 'warning'
  | 'caution'
  | 'classic'

/**
 * Props for `<Aside/>`.
 *
 * @property variant Admonition flavor; picks the label, icon, and
 *           accent. Defaults to `note` so an unadorned `<Aside>` still
 *           renders sensibly.
 * @property children Admonition body (MDX prose).
 */
export interface AsideProps {
  variant?: AsideVariant
  children: ReactNode
}

/**
 * Per-variant chrome: the visible label and the icon beside it. The
 * accent color lives in `aside.module.css` under the same variant key.
 */
const VARIANT_CHROME: Record<
  AsideVariant,
  { label: string; icon: () => JSX.Element }
> = {
  note: { label: 'Note', icon: () => <InfoCircleIcon /> },
  tip: { label: 'Tip', icon: () => <LightbulbIcon /> },
  important: { label: 'Important', icon: () => <ReportIcon /> },
  warning: { label: 'Warning', icon: () => <AlertTriangleIcon /> },
  caution: { label: 'Caution', icon: () => <OctagonAlertIcon /> },
  classic: { label: 'The classic way', icon: () => <HistoryIcon /> },
}

/**
 * A tinted, variant-keyed admonition block (note / tip / important /
 * warning / caution / classic). Authored beside prose or nested inside
 * an `<Objective>` — where it renders under the command it annotates.
 *
 * @param props See {@link AsideProps}.
 * @returns The rendered admonition.
 */
export function Aside(props: AsideProps): JSX.Element {
  const variant = props.variant ?? 'note'
  const chrome = VARIANT_CHROME[variant] ?? VARIANT_CHROME.note

  return (
    <aside
      className={`${styles.aside} ${styles[variant] ?? styles.note}`}
      data-variant={variant}
      aria-label={chrome.label}>
      <span className={styles.label}>
        {chrome.icon()}
        {chrome.label}
      </span>
      <div className={styles.body}>{props.children}</div>
    </aside>
  )
}
